'use client';

import { useRef, useState } from 'react';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import {
  Button2,
  Dialog,
  DialogActions,
  DialogFooter,
  DialogHeader,
  Text,
  TextareaField,
  theme,
  ToggleButton,
  ToggleButtonGroup,
} from '@frameio/vapor';
import { useFeedbackDialogContext } from '../../contexts/ModalContext/ModalContext';
import { useSubmitFeedback } from '../../hooks/useSubmitFeedback';
import TEXT from './generated/TEXT/GlobalFeedbackDialog';
import styles from './GlobalFeedbackDialog.module.css';

TEXT.DEFINE({
  /** Close button label */
  closeLabel: 'Close',
  /** Dialog title */
  title: 'Submit Feedback',
  /** Prompt above the topic chips */
  prompt: "What's on your mind?",
  /** Details textarea placeholder */
  placeholder:
    "We'd love to hear how Frame.io is working for you, and if there's anything we can do to improve your experience.",
  /** Details textarea label */
  addDetails: 'Add details (required)',
  /** Accessible label for the details textarea */
  detailsLabel: 'Additional feedback details',
  /** Cancel button */
  cancel: 'Cancel',
  /** Submit button */
  submit: 'Submit',
  /** Success toast shown after feedback is submitted */
  submitSuccess:
    'Thank you! Your feedback directly helps shape product improvements.',
  /** Error toast shown when feedback submission fails */
  submitError:
    "Sorry, your feedback wasn't submitted due to an error. Please try again.",
  topics: {
    requestFeature: 'Request a feature',
    suggestion: 'Share a suggestion',
    love: 'Share something I love',
    badExperience: 'Report a bad experience',
    confusing: 'Flag something confusing',
    other: 'Other',
  },
});

const FEEDBACK_TOPIC_IDS = [
  'requestFeature',
  'suggestion',
  'love',
  'badExperience',
  'confusing',
  'other',
] as const;

type FeedbackTopic = (typeof FEEDBACK_TOPIC_IDS)[number];

/**
 * Origination and topic summaries are system metadata consumed by the feedback
 * hub automations and must NOT be localized. Origination is kept as
 * 'Feature Request' so this global entry point's Airtable rows stay classified
 * as they were before the modal was redesigned.
 */
const ORIGINATION = 'Feature Request';

/** Summary sent for feedback submitted with details but no topic. */
const GENERAL_FEEDBACK_SUMMARY = 'General feedback';

const TOPIC_SUMMARIES: Record<FeedbackTopic, string> = {
  requestFeature: 'Request a feature',
  suggestion: 'Share a suggestion',
  love: 'Share something I love',
  badExperience: 'Report a bad experience',
  confusing: 'Flag something confusing',
  other: 'Other',
};

type GlobalFeedbackDialogProps = {
  additionalFormData?: Record<string, string>;
};

/**
 * The global "Submit Feedback" modal, opened from the primary nav and account
 * menu via `openFeedbackDialog('featureRequest')`. Mirrors the Labs feedback
 * experience (topic chips + details) while preserving the original
 * feature-request Airtable mapping (topic folds into `summary`).
 */
export function GlobalFeedbackDialog({
  additionalFormData,
}: GlobalFeedbackDialogProps) {
  const { isFeedbackDialogOpen, variant, closeFeedbackDialog } =
    useFeedbackDialogContext();
  const [feedbackText, setFeedbackText] = useState('');
  const [topic, setTopic] = useState<FeedbackTopic | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialFocusRef = useRef<HTMLTextAreaElement>(null);

  const submitFeedback = useSubmitFeedback();
  const tryCatchHandler = useToastTryCatchHandler({ duration: 4000 });

  const isShowing = isFeedbackDialogOpen && variant === 'featureRequest';
  // Details are required; the topic chip is optional.
  const canSubmit = feedbackText.trim().length > 0;

  const handleClose = () => {
    setFeedbackText('');
    setTopic(null);
    setIsSubmitting(false);
    closeFeedbackDialog();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    const summary = topic ? TOPIC_SUMMARIES[topic] : GENERAL_FEEDBACK_SUMMARY;
    const result = await tryCatchHandler({
      attempt: () =>
        submitFeedback({
          origination: ORIGINATION,
          summary,
          description: feedbackText,
          additionalFormData: {
            variant: 'featureRequest',
            ...additionalFormData,
          },
        }),
      successMessage: TEXT.submitSuccess,
      errorMessage: TEXT.submitError,
    });
    // The handler returns the caught error instead of rethrowing — keep the
    // dialog open on failure so the user can retry.
    if (result instanceof Error) {
      setIsSubmitting(false);
      return;
    }
    handleClose();
  };

  return (
    <Dialog
      data-testid="feedback-dialog"
      enableClickOutsideToClose={false}
      hasOutermostTheme={true}
      initialFocusRef={initialFocusRef}
      isShowing={isShowing}
      onRequestClose={handleClose}
      size="medium"
      zIndex={theme.zIndices.modal + 1}
    >
      <DialogHeader closeLabel={TEXT.closeLabel} title={TEXT.title} />
      <div className={styles.body}>
        <div className={styles.topicSection}>
          <Text color="primary" variant="body">
            {TEXT.prompt}
          </Text>
          <ToggleButtonGroup
            aria-label={TEXT.prompt}
            className={styles.topicChips}
          >
            {FEEDBACK_TOPIC_IDS.map((topicId) => (
              <ToggleButton
                key={topicId}
                isSelected={topic === topicId}
                onChange={() => setTopic(topic === topicId ? null : topicId)}
                shape="rounded"
              >
                {TEXT.topics[topicId]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>

        <div className={styles.detailsSection}>
          <Text color="primary" variant="body">
            {TEXT.addDetails}
          </Text>
          <TextareaField
            ref={initialFocusRef}
            aria-label={TEXT.detailsLabel}
            aria-required={true}
            fontSize={13}
            maxLength={5000}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder={TEXT.placeholder}
            resize="vertical"
            rowsMax={8}
            rowsMin={8}
            value={feedbackText}
          />
        </div>
      </div>

      <DialogFooter>
        <DialogActions>
          <Button2 onPress={handleClose}>{TEXT.cancel}</Button2>
          <Button2
            isDisabled={!canSubmit || isSubmitting}
            isLoading={isSubmitting}
            onPress={handleSubmit}
            variant="primary"
          >
            {TEXT.submit}
          </Button2>
        </DialogActions>
      </DialogFooter>
    </Dialog>
  );
}
